import { useState } from 'react';
import { Icon } from '@gftg/ui';
import { ErrorBlock, LoadingBlock, PageHeader } from '../components/states';
import { useEvent, useVenue } from '../lib/queries';

/**
 * The three maps that ship with the app: the conference floor plan, the BoardWalk grounds, and the
 * Walt Disney World property. They live in `public/maps` because this event has a known set of
 * three that will not change, and the screen is a tab each rather than a list of uploaded files.
 */
const MAPS = [
  {
    id: 'conference',
    tab: 'Conference',
    images: [{ src: '/maps/conference.png', label: 'conference center floor plan' }],
  },
  {
    id: 'resort',
    tab: 'Resort',
    images: [{ src: '/maps/resort.webp', label: 'BoardWalk resort map' }],
  },
  {
    id: 'destination',
    tab: 'Destination',
    images: [
      { src: '/maps/destination.webp', label: 'Walt Disney World map' },
      { src: '/maps/directory.webp', label: 'hospitals, stores and services' },
    ],
  },
] as const;

type MapTabId = (typeof MAPS)[number]['id'];

/**
 * Venue and map (module 9). The maps sit at the top at the size they will actually be used at, with
 * a tab each so a phone is not asked to scroll past three unreadable previews to find the one the
 * attendee opened the screen for.
 *
 * The mockup has neither the venue's name nor its address on it. They stay, as the header's
 * subtitle: this is the screen somebody opens in a taxi, and the address is the answer.
 */
export function VenuePage() {
  const event = useEvent();
  const venue = useVenue();
  const [selectedId, setSelectedId] = useState<MapTabId>('conference');

  if (venue.isPending) {
    return <LoadingBlock label="Loading the venue…" />;
  }

  if (venue.isError || !venue.data) {
    return (
      <ErrorBlock
        message="The venue information could not be loaded."
        onRetry={() => void venue.refetch()}
      />
    );
  }

  const { venueName, venueCity, venueAddress, rooms } = venue.data;
  const golfLink = event.data?.links.golf ?? null;
  const selected = MAPS.find((map) => map.id === selectedId) ?? MAPS[0];

  return (
    <>
      <div className="border-b border-border-2 bg-surface">
        <PageHeader
          title="Venue & map"
          subtitle={[venueName, venueCity, venueAddress].filter(Boolean).join(' · ') || undefined}
        />
      </div>

      {/* The map is the screen on a phone and everything else is below it. On a laptop the rooms
          and the golf note fit beside it, which is the pairing somebody actually uses: the plan on
          the left, the name of the room they are looking for on the right. */}
      <main className="px-gutter space-y-card py-4 lg:grid lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)] lg:items-start lg:gap-card lg:space-y-0">
        <section className="lg:row-span-2">
          <div
            role="tablist"
            aria-label="Maps"
            className="-mx-gutter px-gutter mb-3 flex gap-2 overflow-x-auto pb-1"
          >
            {MAPS.map((map) => {
              const active = map.id === selected.id;
              return (
                <button
                  key={map.id}
                  type="button"
                  role="tab"
                  id={`map-tab-${map.id}`}
                  aria-selected={active}
                  aria-controls={`map-panel-${map.id}`}
                  onClick={() => setSelectedId(map.id)}
                  className={`text-chip min-h-[30px] shrink-0 rounded-full px-4 font-semibold ${
                    active
                      ? 'bg-ink text-white'
                      : 'border border-border-input bg-surface text-ink-2'
                  }`}
                >
                  {map.tab}
                </button>
              );
            })}
          </div>

          <div
            role="tabpanel"
            id={`map-panel-${selected.id}`}
            aria-labelledby={`map-tab-${selected.id}`}
            className="space-y-card"
          >
            {selected.images.map((image) => (
              <StaticMapFrame key={image.src} src={image.src} label={image.label} />
            ))}
          </div>
        </section>

        <section className="rounded-card border border-border bg-surface p-4">
          <h2 className="text-eyebrow tracking-eyebrow font-bold text-ink-3 uppercase">
            Rooms in use
          </h2>

          {rooms.length === 0 ? (
            <p className="text-body mt-2 text-ink-3">
              The organizers have not listed the rooms yet.
            </p>
          ) : (
            <ul className="mt-3 space-y-3">
              {rooms.map((room) => (
                <li
                  key={room.id}
                  className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5"
                >
                  <div className="min-w-0">
                    <p className="text-row font-semibold text-ink">{room.name}</p>
                    {/* Not in the mockup, and the reason it is here is Palm Course: a flat list
                        makes a shuttle ride away look like a room down the corridor. */}
                    {room.venueLevel && (
                      <p className="text-eyebrow mt-0.5 text-ink-4">{room.venueLevel}</p>
                    )}
                  </div>
                  {room.usedFor && (
                    <p className="text-subtitle ml-auto text-right text-ink-3">{room.usedFor}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* VEN-01 names the golf course map too. The design leaves it to the golf partner's own
            site, which is the whole of what this screen says about it. */}
        {golfLink && (
          <div className="rounded-card border border-dashed border-border-dashed p-4">
            <p className="text-body text-ink-3">
              The golf course has its own map, on the tournament’s own site.
            </p>
            <a
              href={golfLink}
              target="_blank"
              rel="noreferrer noopener"
              className="text-subtitle mt-2 inline-flex items-center gap-1.5 font-semibold text-brand-text underline"
            >
              Golf tournament
              <Icon name="external" size={12} />
            </a>
          </div>
        )}
      </main>
    </>
  );
}

/**
 * The map itself (VEN-01). It opens full-screen on a tap rather than being zoomable in place: the
 * frame is a quarter of a phone screen and a floor plan is unreadable at that size, so the useful
 * gesture is "make this as big as the phone gets", which is what a plain link to the image does —
 * including the pinch-zoom, which the browser's own image view already has.
 */
function StaticMapFrame({ src, label }: { src: string; label: string }) {
  return (
    <figure className="rounded-card overflow-hidden border border-border bg-surface">
      <a href={src} target="_blank" rel="noreferrer noopener">
        <img
          src={src}
          alt={`The ${label}`}
          // Contained rather than cropped: a floor plan with its edges cut off is the one thing a
          // map may not be, even when the frame's shape and the image's disagree.
          className="aspect-[4/3] w-full bg-brand-wash object-contain"
        />
      </a>
      <figcaption className="text-eyebrow px-4 py-2 text-ink-4">
        {/* "Open" rather than "tap": the same caption is read on a laptop, where there is nothing
            to tap and the gesture is a click. */}
        Open the {label} to see it full size.
      </figcaption>
    </figure>
  );
}
